import { motion } from "framer-motion";

/**
 * Realistic iPhone 15 Pro mockup rendered in pure CSS.
 * The screen displays whatever image path is passed via `src`.
 */
const PhoneMockup = ({ src, alt = "App preview", className = "" }) => {
  return (
    <div
      className={`relative mx-auto ${className}`}
      style={{ width: "300px", aspectRatio: "300 / 610" }}
    >
      {/* Outer bezel */}
      <div
        className="relative w-full h-full rounded-[54px] p-[10px]"
        style={{
          background:
            "linear-gradient(145deg, #1e293b 0%, #0f172a 40%, #020617 100%)",
          boxShadow:
            "0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1.5px rgba(148,163,184,0.18), inset 0 0 0 1px rgba(15,23,42,0.9)",
        }}
      >
        {/* Inner bezel highlight */}
        <div
          className="absolute inset-[3px] rounded-[52px] pointer-events-none"
          style={{
            background:
              "linear-gradient(145deg, rgba(148,163,184,0.14), rgba(15,23,42,0) 30%, rgba(15,23,42,0) 70%, rgba(148,163,184,0.08))",
          }}
        />

        {/* Side buttons */}
        <div className="absolute left-[-2px] top-[110px] w-[3px] h-[28px] bg-slate-800 rounded-l-sm" />
        <div className="absolute left-[-2px] top-[160px] w-[3px] h-[50px] bg-slate-800 rounded-l-sm" />
        <div className="absolute left-[-2px] top-[220px] w-[3px] h-[50px] bg-slate-800 rounded-l-sm" />
        <div className="absolute right-[-2px] top-[145px] w-[3px] h-[70px] bg-slate-800 rounded-r-sm" />

        {/* Screen */}
        <div
          className="relative w-full h-full rounded-[44px] overflow-hidden bg-slate-900"
          style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.6)" }}
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover object-top select-none"
            draggable={false}
          />

          {/* Dynamic Island */}
          <div
            className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[90px] h-[26px] bg-black rounded-full z-10"
            style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.5)" }}
          >
            <div className="absolute right-[10px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full bg-slate-800" />
          </div>

          {/* Subtle screen reflection */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 40%)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Floating variant: adds a gentle continuous idle animation.
 */
export const FloatingPhone = ({ src, alt, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotateX: 8, rotateY: -6 }}
    animate={{ opacity: 1, y: 0, rotateX: 0, rotateY: 0 }}
    transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
    className={className}
    style={{ perspective: 1200 }}
  >
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    >
      <PhoneMockup src={src} alt={alt} />
    </motion.div>
  </motion.div>
);

export default PhoneMockup;
